import { hasYesOrNo } from 'models/validate';
import { DEFAULT_LATEST, THIS_MONTH, TODAY } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { formConfig } from 'config/forms';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import { SubstanceDrugsPrescriptionItemModel } from '../types';

const substanceDrugsPrescriptionItemValidator: ValidateJSModel<SubstanceDrugsPrescriptionItemModel> = {
  PrescriptionName: { presence: true, hasValue: true },
  InvolvementDates: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (!options?.applicantBirthdate) { return {}; }
      if (!value?.to) { return { presence: true }; }
      const applicantBirthdate: SafeDateTime = getSafeDateTime(options?.applicantBirthdate);
      const endDate: SafeDateTime = getSafeDateTime(value.to);
      const sixteenthBirthdate: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 16 }));
      const twentyFirstBirthday: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 21 }));
      const fiveYearsAgo: SafeDateTime = getSafeDateTime(DEFAULT_LATEST.minus({ years: 5 }));

      // if 21 bday is before (or on) today applicant is over 21
      const isOverTwentyOne: boolean = isSafeDateLater(getSafeDateTime(DEFAULT_LATEST), twentyFirstBirthday);
      // five years for 21 yoe, any time after 16 for under age.
      const stauteOfLimitation: SafeDateTime = isOverTwentyOne ? fiveYearsAgo : sixteenthBirthdate;

      const isEndDateBeforeStatue: boolean = isSafeDateLater(stauteOfLimitation, endDate);

      return {
        presence: true,
        daterange: {
          requireYear: false,
          earliest: isEndDateBeforeStatue ? setToStartOfMonth(stauteOfLimitation).datetime : applicantBirthdate?.datetime,
          tooEarly: isEndDateBeforeStatue ? 'error.beforeDuration' : 'error.applicantMin',
          latest: DEFAULT_LATEST
        },
      };
    }

    const formTypeConfig = options?.formType && formConfig[options?.formType as keyof typeof formConfig];
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && getSafeDateTime(getSafeDateTime(options?.applicantBirthdate)?.datetime?.plus({ years: 18 }));
    const years: any = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_USE_YEARS;
    let beyondRequired: SafeDateTime = getSafeDateTime(THIS_MONTH);
    let useDuration = false;

    if (years && beyondRequired?.valid && typeof years === 'number') {
      if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid
      && applicantEighteenthBirthdate.datetime.year < (TODAY.year - years)) {
        beyondRequired = setToStartOfMonth(applicantEighteenthBirthdate);
      } else {
        beyondRequired.datetime = beyondRequired?.datetime.minus({ years });
      }

      useDuration = !value?.present && isSafeDateLater(beyondRequired, setToStartOfMonth(getSafeDateTime(value?.to)));
    }

    return {
      presence: true,
      daterange: {
        requireYear: false,
        earliest: useDuration ? setToStartOfMonth(beyondRequired).datetime : setToStartOfMonth(options?.applicantBirthdate),
        tooEarly: useDuration ? 'error.beforeDuration' : 'error.applicantMin',
        latest: DEFAULT_LATEST
      },
    };
  },

  HowManyTimes: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },

  Reason: { presence: true, hasValue: true },
  UseWhileEmployed: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options && options?.requireUseWhileEmployed === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  UseWhileEmployedPosition: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.UseWhileEmployed?.value === 'Yes') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  UseWhileEmployedExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.UseWhileEmployed?.value === 'Yes') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  UseWithClearance: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options && options?.requireUseWithClearance === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  UseWithClearanceExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.UseWithClearance?.value === 'Yes') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  UseInFuture: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    return { };
  },
  UseInFutureExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Comments: {},
};

export default substanceDrugsPrescriptionItemValidator;
